import React from 'react';
import { useAggregatedAvailability } from './hooks/useAggregatedAvailability';
import { AggregatedSlot, PMCConfig } from '@kyruus/types';
import { getVisibility } from '../../utils/search-common';
import { AggregatedAvailability } from '@kyruus/scheduling-components';
import { FormattedMessage } from '@kyruus/intl';
import messages from '../messages';
import {
  AppointmentAsideStickyBox,
  AppointmentAsideContainer,
  ScheduleAppointmentTitle,
  ContactSection
} from './styles';
import { getContactForLocation, getSelectedAppointmentHref } from './utils';
import { EmergencyDisclaimer } from '@kyruus/provider-components';
import { Link } from '@biome-design-system/ui/Link';
import { MarketableLocation } from '../../types';
import { useHistory, useLocation } from 'react-router-dom';
import { LoadingAppointmentAside } from './LoadingAppointmentAside';

export type AppointmentAsideProps = {
  config: PMCConfig;
  location: MarketableLocation;
  customerCode: string;
  productName: string;
};

export const AppointmentAside = ({
  config,
  customerCode,
  productName,
  location
}: AppointmentAsideProps) => {
  const { schedulingVisibility } = getVisibility(config);
  const { id: primaryLocationId, name: locationName } = location;

  const reactRouterLocation = useLocation();
  const history = useHistory();

  const {
    locationProviders = [],
    locationIds: primaryLocationIds,
    searchClient,
    loading: providersLoading,
    fetchAvailabilitySlots,
    fetchPurposeOptions
  } = useAggregatedAvailability({
    config,
    primaryLocationId: location.id,
    customerCode,
    productName,
    schedulingVisibility
  });

  //TODO: Put in tracking events
  const onCloseEmergencyDisclaimer = () => {};
  const onClickEmergencyDisclaimer = (_event: any) => {};

  const handleAppointmentSlotSelected = (slot: AggregatedSlot) => {
    const href = getSelectedAppointmentHref(
      slot,
      locationProviders,
      reactRouterLocation,
      config
    );
    history.push(href);
  };

  // Providers or provider locations should never be empty after loading,
  // but if there is an error or an issue fetching providers we don't want to show the aside.
  if (
    !providersLoading &&
    locationProviders.length === 0 &&
    primaryLocationIds.length === 0
  ) {
    return <></>;
  }

  const primaryPhone = getContactForLocation(location);
  return (
    <AppointmentAsideStickyBox>
      <AppointmentAsideContainer>
        <ScheduleAppointmentTitle
          id="LocationAsideAppointmentTitle"
          data-testid="location-appointment-title"
        >
          <FormattedMessage {...messages.AppointmentTitle} />
        </ScheduleAppointmentTitle>
        <LoadingAppointmentAside isLoading={providersLoading} />
        {!providersLoading && (
          <>
            <EmergencyDisclaimer
              config={config?.direct_book}
              data-testid="emergency-disclaimer"
              onClose={onCloseEmergencyDisclaimer}
              onClick={onClickEmergencyDisclaimer}
              sx={(theme) => ({
                marginBottom: theme.spacing(3)
              })}
            />
            <AggregatedAvailability
              config={config}
              customerCode={customerCode}
              locationName={locationName}
              providers={locationProviders}
              services={{
                fetchPurposeOptions,
                handleAppointmentSlotSelected,
                fetchAvailabilitySlots
              }}
              locationIds={primaryLocationIds}
              primaryLocationId={primaryLocationId}
              productName={productName}
              searchClient={searchClient}
              setDefaultPurpose={true}
            />
            {primaryPhone && (
              <ContactSection>
                <FormattedMessage
                  {...messages.NeedHelpContact}
                  values={{
                    phone: (
                      <Link href={`tel:${primaryPhone}`}>{primaryPhone}</Link>
                    )
                  }}
                ></FormattedMessage>
              </ContactSection>
            )}
          </>
        )}
      </AppointmentAsideContainer>
    </AppointmentAsideStickyBox>
  );
};
